import { DrawingModel, type Anchor, type Handle } from "../DrawingModel";
import type { Chart } from "../../chart/Chart";
import { distToSegment, distToLine } from "../geometry";
import { extendToBox, rayEnd, drawArrowHead } from "./helpers";

const TOL = 6;

export class TrendLine extends DrawingModel {
  type = "trendline";
  render(ctx: CanvasRenderingContext2D, chart: Chart) {
    const [a, b] = this.anchors;
    this.applyStroke(ctx);
    ctx.beginPath();
    ctx.moveTo(this.X(a, chart), this.Y(a, chart));
    ctx.lineTo(this.X(b, chart), this.Y(b, chart));
    ctx.stroke();
    ctx.setLineDash([]);
  }
  hitTest(px: number, py: number, chart: Chart) {
    const [a, b] = this.anchors;
    return distToSegment(px, py, this.X(a, chart), this.Y(a, chart), this.X(b, chart), this.Y(b, chart)) <= TOL;
  }
}

export class Ray extends DrawingModel {
  type = "ray";
  render(ctx: CanvasRenderingContext2D, chart: Chart) {
    const [a, b] = this.anchors;
    const x1 = this.X(a, chart),
      y1 = this.Y(a, chart),
      x2 = this.X(b, chart),
      y2 = this.Y(b, chart);
    const [ex, ey] = rayEnd(x1, y1, x2, y2);
    this.applyStroke(ctx);
    ctx.beginPath();
    ctx.moveTo(x1, y1);
    ctx.lineTo(ex, ey);
    ctx.stroke();
    ctx.setLineDash([]);
  }
  hitTest(px: number, py: number, chart: Chart) {
    const [a, b] = this.anchors;
    const x1 = this.X(a, chart),
      y1 = this.Y(a, chart),
      x2 = this.X(b, chart),
      y2 = this.Y(b, chart);
    const [ex, ey] = rayEnd(x1, y1, x2, y2);
    return distToSegment(px, py, x1, y1, ex, ey) <= TOL;
  }
}

export class ExtendedLine extends DrawingModel {
  type = "extended";
  render(ctx: CanvasRenderingContext2D, chart: Chart) {
    const [a, b] = this.anchors;
    const x1 = this.X(a, chart),
      y1 = this.Y(a, chart),
      x2 = this.X(b, chart),
      y2 = this.Y(b, chart);
    const [sx, sy, ex, ey] = extendToBox(x1, y1, x2, y2, chart.plotWidth, chart.plotHeight);
    this.applyStroke(ctx);
    ctx.beginPath();
    ctx.moveTo(sx, sy);
    ctx.lineTo(ex, ey);
    ctx.stroke();
    ctx.setLineDash([]);
  }
  hitTest(px: number, py: number, chart: Chart) {
    const [a, b] = this.anchors;
    return distToLine(px, py, this.X(a, chart), this.Y(a, chart), this.X(b, chart), this.Y(b, chart)) <= TOL;
  }
}

export class Arrow extends DrawingModel {
  type = "arrow";
  render(ctx: CanvasRenderingContext2D, chart: Chart) {
    const [a, b] = this.anchors;
    const x1 = this.X(a, chart),
      y1 = this.Y(a, chart),
      x2 = this.X(b, chart),
      y2 = this.Y(b, chart);
    this.applyStroke(ctx);
    ctx.beginPath();
    ctx.moveTo(x1, y1);
    ctx.lineTo(x2, y2);
    ctx.stroke();
    ctx.setLineDash([]);
    drawArrowHead(ctx, x1, y1, x2, y2, 8 + this.style.width * 2.5, this.style.color);
  }
  hitTest(px: number, py: number, chart: Chart) {
    const [a, b] = this.anchors;
    return distToSegment(px, py, this.X(a, chart), this.Y(a, chart), this.X(b, chart), this.Y(b, chart)) <= TOL;
  }
}

export class HorizontalLine extends DrawingModel {
  type = "hline";
  get points() {
    return 1;
  }
  render(ctx: CanvasRenderingContext2D, chart: Chart) {
    const a = this.anchors[0];
    const y = this.Y(a, chart);
    this.applyStroke(ctx);
    ctx.beginPath();
    ctx.moveTo(0, y);
    ctx.lineTo(chart.plotWidth, y);
    ctx.stroke();
    ctx.setLineDash([]);
    // price tag (inside plot, right-aligned)
    ctx.font = "11px -apple-system, system-ui, sans-serif";
    ctx.textAlign = "left";
    ctx.textBaseline = "middle";
    const label = a.price.toFixed(chart.priceDecimals);
    const tw = ctx.measureText(label).width + 10;
    ctx.fillStyle = this.style.color;
    ctx.fillRect(chart.plotWidth - tw - 2, y - 8, tw, 16);
    ctx.fillStyle = "#fff";
    ctx.fillText(label, chart.plotWidth - tw + 3, y + 0.5);
  }
  handles(chart: Chart): Handle[] {
    const a = this.anchors[0];
    return [{ x: this.X(a, chart), y: this.Y(a, chart), kind: 0 }];
  }
  hitTest(px: number, py: number, chart: Chart) {
    return Math.abs(py - this.Y(this.anchors[0], chart)) <= TOL;
  }
}

export class VerticalLine extends DrawingModel {
  type = "vline";
  get points() {
    return 1;
  }
  render(ctx: CanvasRenderingContext2D, chart: Chart) {
    const a = this.anchors[0];
    const x = this.X(a, chart);
    this.applyStroke(ctx);
    ctx.beginPath();
    ctx.moveTo(x, 0);
    ctx.lineTo(x, chart.plotHeight);
    ctx.stroke();
    ctx.setLineDash([]);
  }
  handles(chart: Chart): Handle[] {
    const a = this.anchors[0];
    return [{ x: this.X(a, chart), y: this.Y(a, chart), kind: 0 }];
  }
  hitTest(px: number, py: number, chart: Chart) {
    return Math.abs(px - this.X(this.anchors[0], chart)) <= TOL;
  }
}
